import React, { useEffect, useRef, useState } from "react";
import { useNavigationType } from "react-router-dom";
import "../../styles/home.css";
import OriginStatsStrip from "./OriginStatsStrip";

import webleedcreative1 from "../../assets/images/homepage/welcometohkimedia/webuiltcreative1.jpg";
import webleedcreative2 from "../../assets/images/homepage/welcometohkimedia/webuiltcreative2.jpg";
import webreedcreative1 from "../../assets/images/homepage/welcometohkimedia/webreedcreative1.jpg";
import webreedcreative2 from "../../assets/images/homepage/welcometohkimedia/webreedcreative2.jpg";

// The two beats shown one at a time in the same spot under the title.
const STEPS = [
  {
    num: "01",
    tagline: "We Bleed Creative",
    desc: "Every campaign starts with an idea we actually believe in, not a template.",
    images: [webleedcreative1, webleedcreative2],
    alt: "HKI Media — we bleed creative",
  },
  {
    num: "02",
    tagline: "We Breed Creative",
    desc: "One good idea becomes a system content, campaigns and experiences that keep it alive.",
    images: [webreedcreative1, webreedcreative2],
    alt: "HKI Media — we breed creative",
  },
];

const LEDE_MAIN =
  "HKI Media started with a simple belief: great ideas deserve better execution. We kept seeing brands juggle multiple agencies, endless follow-ups, and disconnected teams. Somewhere along the way, the original idea would lose its spark.";

const LEDE_DIM =
  "So we built an agency that works differently. Strategy, creativity, production, and marketing all under one roof. Fewer layers, faster decisions, and work that's built to make an impact.";

const ORIGIN_NOTES = [
  {
    num: "01",
    pin: "#e0102a",
    rot: "-4deg",
    mt: "0px",
    text: "Strategy lived with one team, content with another, and production somewhere else. By the time everything came together, the idea had lost the spark that made it worth pursuing in the first place.",
  },
  {
    num: "02",
    pin: "#e85d2f",
    rot: "3deg",
    mt: "40px",
    text: "So we built one creative ecosystem. Strategy, content, marketing, production, and experiences all working as one. No silos, no endless handoffs, just one team shaping every idea from the first brief to the final execution.",
  },
  {
    num: "03",
    pin: "#e0102a",
    rot: "-2deg",
    mt: "20px",
    text: "Today, we're a team of 50+ creatives, building brands, campaigns, and experiences that people don't just see they remember.",
  },
];

const DELAY_STEP = 0.08;
const delayStyle = (index) => ({ transitionDelay: `${index * DELAY_STEP}s` });

// Notes pop in on their own, wider cadence (rule/title/lede stay on the
// tight DELAY_STEP above) so each card visibly lands one at a time instead
// of all three arriving within the same 80ms.
const NOTES_START = 0.35;
const NOTE_STEP = 0.22;
const noteDelayStyle = (i) => ({ transitionDelay: `${NOTES_START + i * NOTE_STEP}s` });
const CLOSING_DELAY = NOTES_START + ORIGIN_NOTES.length * NOTE_STEP + 0.15;
const closingDelayStyle = () => ({ transitionDelay: `${CLOSING_DELAY}s` });
const statsDelayStyle = () => ({ transitionDelay: `${CLOSING_DELAY + 0.15}s` });

const SCROLL_KEYS = new Set([" ", "ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"]);
// Soft scroll hold during the intro: input is swallowed until the page has
// sat on the section for LOCK_GRACE_MS (lets trackpad momentum die out),
// then a scroll this deliberate releases it.
const LOCK_GRACE_MS = 700;
const WHEEL_RELEASE_PX = 400; // ~4 mouse-wheel notches in one gesture
const WHEEL_GESTURE_GAP_MS = 600;
const TOUCH_RELEASE_PX = 90;

const isReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Whether the homepage has been shown before in this app session (resets on
// a full page load).
let shownBefore = false;

const WelcomeToHkiMedia = () => {
  const sectionRef = useRef(null);
  const navigationType = useNavigationType();
  // The intro plays on every visit (fresh load, reload or a link to the
  // homepage). Coming back with Back/Forward shows its finished state instead,
  // so the page is its full height and MainLayout can return the visitor to
  // where they were rather than pulling them back into the intro.
  const [done] = useState(() => (navigationType === "POP" && shownBefore) || isReducedMotion());
  useEffect(() => {
    shownBefore = true;
  }, []);
  const [showWhoWeAre, setShowWhoWeAre] = useState(false);
  const [showTitle, setShowTitle] = useState(done);
  // 0 = none, 1 = "01", 2 = "02", 3 = done (both faded out)
  const [step, setStep] = useState(done ? 3 : 0);
  const [showOrigin, setShowOrigin] = useState(done);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || done) return undefined;

    const timers = [];
    let elapsed = 0;
    const next = (delay, fn) => {
      elapsed += delay;
      timers.push(setTimeout(fn, elapsed));
    };

    // Hold the page on this section while the intro plays, so a casual
    // scroll (or the momentum of the one that brought the user here) can't
    // carry it past half-seen. It's a soft hold: once the page has settled,
    // a deliberate scroll up or down — a few wheel notches, a real swipe, a
    // scroll key or a scrollbar drag — lets the user go.
    let lockY = 0;
    let arrivedAt = null;
    let pull = 0;
    let lastWheelAt = 0;
    let lastInputAt = 0;
    let touchStartY = null;

    const isSettled = () =>
      arrivedAt !== null && performance.now() - arrivedAt > LOCK_GRACE_MS;

    const onWheel = (e) => {
      lastInputAt = performance.now();
      if (!isSettled()) {
        e.preventDefault();
        return;
      }
      const now = performance.now();
      const delta = e.deltaY * (e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? window.innerHeight : 1);
      // A pause or a change of direction starts a new gesture.
      if (now - lastWheelAt > WHEEL_GESTURE_GAP_MS || Math.sign(delta) !== Math.sign(pull)) pull = 0;
      lastWheelAt = now;
      pull += delta;
      if (Math.abs(pull) > WHEEL_RELEASE_PX) {
        unlockScroll();
        return;
      }
      e.preventDefault();
    };
    const onTouchStart = (e) => {
      touchStartY = e.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (e) => {
      lastInputAt = performance.now();
      const y = e.touches[0]?.clientY;
      if (isSettled() && touchStartY !== null && y !== undefined && Math.abs(y - touchStartY) > TOUCH_RELEASE_PX) {
        unlockScroll();
        return;
      }
      e.preventDefault();
    };
    const onKeyDown = (e) => {
      if (!SCROLL_KEYS.has(e.key)) return;
      if (e.target.closest?.("input, textarea, select, [contenteditable]")) return;
      lastInputAt = performance.now();
      if (isSettled()) {
        unlockScroll();
        return;
      }
      e.preventDefault();
    };
    const onScroll = () => {
      if (arrivedAt === null) {
        if (Math.abs(window.scrollY - lockY) < 2) arrivedAt = performance.now();
        return;
      }
      if (Math.abs(window.scrollY - lockY) <= 1) return;
      // With no wheel/touch/key input just before it, this is the scrollbar
      // being dragged: deliberate once settled, so let go. Otherwise it's
      // input the browser wouldn't let us cancel (wheel events can arrive
      // uncancelable), so snap back.
      // "instant": the page sets scroll-behavior: smooth, which would turn
      // the snap-back into a slow glide the user can out-scroll.
      if (isSettled() && performance.now() - lastInputAt > 250) unlockScroll();
      else window.scrollTo({ top: lockY, behavior: "instant" });
    };
    const lockScroll = () => {
      lockY = el.getBoundingClientRect().top + window.scrollY;
      arrivedAt = Math.abs(window.scrollY - lockY) < 2 ? performance.now() : null;
      window.addEventListener("wheel", onWheel, { passive: false });
      window.addEventListener("touchstart", onTouchStart, { passive: true });
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("keydown", onKeyDown);
      window.addEventListener("scroll", onScroll);
    };
    const unlockScroll = () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        // Run the intro once per visit.
        observer.disconnect();

        setShowWhoWeAre(true);
        lockScroll();
        window.scrollTo({ top: lockY, behavior: "smooth" });

        next(1700, () => setShowWhoWeAre(false));
        next(750, () => setShowTitle(true));
        next(950, () => setStep(1));
        next(3700, () => setStep(2));
        next(3700, () => setStep(3));
        // The origin story opens right under the title, so the page stays
        // where it is; just hand scrolling back to the user.
        next(900, () => {
          setShowOrigin(true);
          unlockScroll();
        });
      },
      { threshold: 0.05 }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
      unlockScroll();
    };
  }, [done]);

  return (
    <section className="bv" id="about" ref={sectionRef}>
      {/* Fills the first screen and centres the intro, so the origin story
          opening below can't shift it. */}
      <div className={`bv-intro ${showOrigin ? "is-collapsed" : ""}`}>
        <div className="bv-animated">
          {/* Large screens: the step's images flank the text column. */}
          {STEPS.map((s, i) =>
            s.images.map((src, j) => (
              <div
                key={src}
                className={`bv-side-image ${j ? "bv-side-right" : "bv-side-left"} ${step === i + 1 ? "is-visible" : ""}`}
              >
                <img src={src} alt={s.alt} />
                <div className="owx-card-duotone" />
              </div>
            ))
          )}

          <div className="bv-text-column">
            <div className="bv-hero">
              <p className={`bv-question ${showWhoWeAre ? "is-active" : ""}`}>Who We Are ?</p>
              <h2 className={`bv-answer ${showTitle ? "is-active" : ""}`}>HKI Media</h2>
            </div>

            {/* Both steps share one grid cell, so 02 replaces 01 in place and
                the space is reserved up front: nothing on the page shifts. */}
            <div className="bv-stage">
              {STEPS.map((s, i) => (
                <div key={s.num} className={`bv-reveal-block ${step === i + 1 ? "is-visible" : ""}`}>
                  <span className="bv-block-index">{s.num}</span>
                  <p className="bv-tagline">{s.tagline}</p>
                  <p className="bv-stage-desc">{s.desc}</p>
                  {/* Smaller screens: the images sit under the text instead. */}
                  <div className="bv-inline-image-pair">
                    {s.images.map((src) => (
                      <div key={src} className={`bv-inline-image ${step === i + 1 ? "is-visible" : ""}`}>
                        <img src={src} alt={s.alt} />
                        <div className="owx-card-duotone" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`bv-collapse ${showOrigin ? "is-open" : ""}`}>
        <div className="bv-collapse-inner">
          <div className={`bv-panel ${showOrigin ? "is-visible" : ""}`}>
            <div className="bv-inner">
              <div className="bv-rule bv-origin-item" style={delayStyle(0)} />

              <div className="bv-story">
                <h3 className="bv-story-title bv-origin-item" style={delayStyle(1)}>
                  The Origin Story
                </h3>
                <p className="bv-lede bv-origin-item" style={delayStyle(2)}>
                  {LEDE_MAIN} <span className="bv-lede-dim">{LEDE_DIM}</span>
                </p>
                <div className="bv-notes">
                  {ORIGIN_NOTES.map((note, i) => (
                    <div
                      className="bv-note bv-origin-item"
                      style={{ ...noteDelayStyle(i), "--note-rot": note.rot, marginTop: note.mt }}
                      key={note.num}
                    >
                      <span className="bv-note-pin" aria-hidden="true">
                        <span className="bv-note-pin-dot" style={{ "--pin-color": note.pin }} />
                        <span className="bv-note-pin-string" style={{ "--pin-color": note.pin }} />
                      </span>
                      <div className="bv-note-card">
                        <span className="bv-note-num" style={{ "--pin-color": note.pin }}>
                          {note.num}
                        </span>
                        <p className="bv-note-text">{note.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="bv-closing bv-origin-item" style={closingDelayStyle()}>
                  Beyond Impressions. <span className="bv-closing-dim">Beyond Expectations.</span>
                </p>

                <OriginStatsStrip style={statsDelayStyle()} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WelcomeToHkiMedia;
